import { FC, useEffect, useState } from 'react';
import {
	// Button,
	Input,
	Table,
	Tooltip,
} from 'antd';

import { Search, DataGrid, Button, Icon } from '@hydrodigital/design-system';

import { EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import AlertService from '../../domain/Alerts/AlertService';
import { Alert } from '@/domain/Alerts/Alert';
import useDebounce from '@/hooks/useDebounce';
import ConfirmationModal from '@/components/Modal/ConfirmationModal';

const alertService = new AlertService();

const AlertsView: FC = () => {
	const [alerts, setAlerts] = useState<Alert[]>();
	const [name, setName] = useState<string>('');
	const [paginationState, setPaginationState] = useState({
		pageIndex: 0,
		pageSize: 10,
	});
	const [totalRows, setTotalRows] = useState(0);
	const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
	const [showConfirmDelete, setShowConfirmDelete] = useState(false);
	const navigate = useNavigate();

	const debouncedName = useDebounce(name, 500);

	const handleDeleteAlert = async () => {
		if (!selectedAlert) return;
		try {
			await alertService.deleteAlert(selectedAlert.alertId!);
			setShowConfirmDelete(false);
			setSelectedAlert(null);
			handleGetAlerts();
		} catch (error) {
			setShowConfirmDelete(false);
		}
	};

	const handleGetAlerts = async () => {
		try {
			const res = await alertService.getAlerts({
				name: debouncedName || null,
				enable: true,
				page: paginationState.pageIndex + 1,
				size: paginationState.pageSize,
			});
			console.log('🚀 ~ handleGetAlerts ~ res:', res);
			setAlerts(res.alerts);
			setTotalRows(res.totalRecords);
		} catch (error) {
			setAlerts([]);
		}
	};

	const gridColumns = [
		{
			header: 'Alert name',
			accessorKey: 'alertName',
			cell: ({ cell }: any) => <span className="table-data-gis">{cell.getValue()}</span>,
		},
		{
			header: 'Updated by',
			id: 'createdBy',
			cell: ({ row }: any) => {
				const { alertCreatedBy, alertCreatedAt } = row.original;
				return (
					<div className="flex column">
						<span style={{ color: '#232B36', fontSize: '14px', fontWeight: '500' }}>
							{alertCreatedBy}
						</span>
						<span style={{ color: '#74788D' }}>
							{dayjs(alertCreatedAt).format('YYYY/MM/DD [at] hh:mm a')}
						</span>
					</div>
				);
			},
		},
		{
			header: 'Actions',
			id: 'actions',
			cell: ({ row }: any) => {
				const record = row.original;
				return (
					<div className='flex items-center'>
						<Tooltip title="Edit">
							<Icon 
								name="edit-alt"
								style={{ cursor: 'pointer', fontSize: '1.5rem' }}
								onClick={() => {
									navigate(`/alerts/form/${record.alertId}`);
								}}
							/>
						</Tooltip>
						<Tooltip title="Delete">
							<Icon
								style={{ fontSize: '1.5rem', cursor: 'pointer' }}
								name="trash-alt"
								onClick={() => {
									setSelectedAlert(record);
									setShowConfirmDelete(true);
								}}
							/>
						</Tooltip>
					</div>
				);
			},
		},
	];

	const columns: ColumnsType<Alert> = [
		{
			title: 'Alert name',
			key: 'name',
			render: (_, { alertName }) => <span className="table-data-gis">{alertName}</span>,
		},
		{
			title: 'Updated by',
			key: 'createdBy',
			render: (_, { alertCreatedAt, alertCreatedBy }) => (
				<div className="flex column">
					<span
						style={{
							color: '#232B36',
							fontSize: '14px',
							fontWeight: '500',
						}}
					>
						{alertCreatedBy}
					</span>
					<span
						style={{
							color: '#74788D',
						}}
					>
						{dayjs(alertCreatedAt).format('YYYY/MM/DD [at] hh:mm a')}
					</span>
				</div>
			),
		},
		{
			title: 'Actions',
			key: 'id',
			render: (_, record) => (
				<div className="text-center">
					<Tooltip title="Edit">
						<EditOutlined
							className="mr-1"
							style={{ cursor: 'pointer', fontSize: '1.5rem' }}
							onClick={() => {
								navigate(`/alerts/form/${record.alertId}`);
							}}
						/>
					</Tooltip>
					<Tooltip title="Delete">
						<Icon
							style={{ fontSize: '1.5rem' }}
							name="trash-alt"
							onClick={() => {
								//   setSelectedDiagram(record);
								//   setShowConfirmDelete(true);
							}}
						/>
					</Tooltip>
				</div>
			),
		},
	];

	useEffect(() => {
		handleGetAlerts();
	}, [paginationState.pageIndex, paginationState.pageSize, debouncedName]);

	useEffect(() => {
		setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
	}, [debouncedName]);

	return (
		<div className="px-5 flex flex-col" style={{ height: 'calc(100vh - 140px)' }}>
			<div className="text-title-page mb-2">Alerts</div>
			<div className="flex justify-between" style={{ flexShrink: 0 }}>
				<div className="flex">
					<Search
						size="small"
						placeholder="Search"
						value={name}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setName(e.target.value);
						}}
						showClearButton={true}
						onClear={() => {
							setName('');
						}}
					/>
					{/* <Select
            style={{ marginLeft: 30, width: 140 }}
            placeholder="In use"
            optionFilterProp="children"
            options={[
              {
                value: true,
                label: "True",
              },
              {
                value: false,
                label: "False",
              },
            ]}
            onChange={(value) => {
              setEnabled(value === undefined ? null : value);
            }}
            allowClear
          /> */}
				</div>
				<div>
					<Button
						size="small"
						style={{
							fontWeight: 700,
							fontSize: 14,
							backgroundColor: '#F7F9FA',
							color: '#434B56',
							border: '1px solid #C4CCD5',
						}}
						label="Add Alert"
						className="mr-5 boton-inicio flex items-center"
						onClick={() => {
							navigate(`/alerts/form`);
						}}
					/>
				</div>
			</div>

			<DataGrid
				data={alerts || []}
				columns={gridColumns}
				className="mt-3"
				stickyHeader={true}
				maxHeight="calc(100vh - 260px)"
				pagination={{
					enabled: true,
					manualPagination: true,
					rowCount: totalRows,
					defaultPageSize: 10,
					pageSizeOptions: [10, 20, 50],
				}}
				paginationState={paginationState}
				onPaginationChange={setPaginationState}
			/>

			<ConfirmationModal
				title="Delete Alert?"
				message="Are you sure you want to delete this alert?"
				showModal={showConfirmDelete}
				onAccept={handleDeleteAlert}
				oncancel={() => {
					setShowConfirmDelete(false);
					setSelectedAlert(null);
				}}
			/>
		</div>
	);
};
export default AlertsView;
